import type { ReactNode } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Pagina } from '@malgoos/shared';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { BotaoAbrirLinha, textoDoNo } from '../../componentes/tabela';
import { get } from '../../nucleo/api';
import { dataHora } from '../../nucleo/formato';
import { jsonCensurado } from '../../nucleo/censura-json';
import { useDiscreto } from '../../componentes/sensivel';
import { carregarMais } from '../../nucleo/foco';

export { BotaoCopiar, copiarTexto, LinhaCopiavel } from '../../componentes/copiar';
import { BotaoCopiar } from '../../componentes/copiar';

/** Bloco de código com rolagem própria (a página nunca rola na horizontal) e botão de copiar. */
export function BlocoCodigo({ codigo, rotuloCopiar, rotulo }: { codigo: string; rotuloCopiar?: string; rotulo?: string }) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0 rounded-lg border border-borda bg-sup-2">
      <div className="flex justify-end border-b border-borda px-2 py-1.5">
        <BotaoCopiar texto={codigo} rotulo={rotuloCopiar} />
      </div>
      {/* r8 visual B8: a rolagem própria do código recebe o foco (setas rolam pelo teclado) e tem nome. */}
      <pre tabIndex={0} role="region" aria-label={rotulo ?? t('comum.codigo')} className="max-w-full overflow-x-auto p-3 font-mono text-xs leading-relaxed text-tinta-1 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">{codigo}</pre>
    </div>
  );
}

export function Chips({ itens, max = 4 }: { itens: string[]; max?: number }) {
  const mostrados = itens.slice(0, max);
  return (
    <span className="flex min-w-0 flex-wrap gap-1">
      {mostrados.map((i) => (
        <Selo key={i}>{i}</Selo>
      ))}
      {itens.length > max && <Selo>{`+${itens.length - max}`}</Selo>}
    </span>
  );
}

export const corDoStatusHttp = (s: number | null): CorSelo => (s === null ? 'neutro' : s >= 200 && s < 300 ? 'verde' : s >= 500 ? 'perigo' : 'laranja');

/**
 * JSON cru (payload, argumentos, resultado): no modo discreto sai censurado por chave (`nucleo/censura-json.ts`). `entidade`:
 * de onde vem o JSON, quando se sabe (review r21 spec M2: o payload de um conector de WhatsApp).
 */
export function Json({ valor, entidade }: { valor: unknown; entidade?: string | null }) {
  useDiscreto();
  return <pre className="max-h-64 max-w-full overflow-auto rounded-lg border border-borda bg-sup-2 p-3 font-mono text-xs text-tinta-1">{jsonCensurado(valor, entidade)}</pre>;
}

export const Quando = ({ v }: { v: string | null }) => {
  const { t } = useTranslation('integracoes');
  return <span className="numero whitespace-nowrap text-tinta-2">{v ? dataHora(v) : t('comum.nunca')}</span>;
};

export interface ColunaLista<T> {
  chave: string;
  rotulo: string;
  render: (l: T) => ReactNode;
  alinhar?: 'direita';
}

/**
 * Lista paginada por cursor (log de entregas, eventos recebidos, chamadas). Tabela no desktop,
 * cartões no celular. Trata carregando, erro e vazio.
 */
export function ListaPaginada<T extends { id: string }>({
  caminho,
  params,
  colunas,
  vazio,
  aoClicar,
  chaveConsulta,
}: {
  caminho: string;
  params?: Record<string, unknown>;
  colunas: ColunaLista<T>[];
  vazio: string;
  aoClicar?: (l: T) => void;
  chaveConsulta: unknown[];
}) {
  const { t } = useTranslation();
  const q = useInfiniteQuery({
    queryKey: [...chaveConsulta, caminho, params],
    queryFn: ({ pageParam }) => get<Pagina<T>>(caminho, { ...params, cursor: pageParam, limite: 20 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
  });
  const linhas = q.data?.pages.flatMap((p) => p.itens) ?? [];
  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  if (!linhas.length) return <Vazio texto={vazio} />;
  const principal = colunas[0]!;
  return (
    <div className="min-w-0">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-borda text-left">
              {colunas.map((c) => (
                <th key={c.chave} scope="col" className={`px-4 py-2.5 font-medium whitespace-nowrap text-tinta-3 ${c.alinhar === 'direita' ? 'text-right' : ''}`}>
                  {c.rotulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.id} onClick={aoClicar ? () => aoClicar(l) : undefined} className={`border-b border-borda last:border-0 ${aoClicar ? 'cursor-pointer hover:bg-sup-2' : ''}`}>
                {colunas.map((c) => (
                  <td key={c.chave} className={`px-4 py-2.5 ${c.alinhar === 'direita' ? 'text-right' : ''}`}>
                    {aoClicar && c === principal ? <BotaoAbrirLinha aoAbrir={() => aoClicar(l)}>{c.render(l)}</BotaoAbrirLinha> : c.render(l)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="divide-y divide-borda md:hidden">
        {linhas.map((l) => (
          <li key={l.id}>
            <button type="button" disabled={!aoClicar} onClick={() => aoClicar?.(l)} className="flex w-full min-w-0 flex-col gap-1 px-4 py-3 text-left enabled:hover:bg-sup-2">
              <span className="min-w-0 truncate font-medium" title={textoDoNo(principal.render(l)) || undefined}>
                {principal.render(l)}
              </span>
              {colunas.slice(1, 5).map((c) => (
                <span key={c.chave} className="flex min-w-0 justify-between gap-3 text-xs text-tinta-3">
                  <span className="shrink-0">{c.rotulo}</span>
                  <span className="min-w-0 truncate text-right text-tinta-2" title={textoDoNo(c.render(l)) || undefined}>
                    {c.render(l)}
                  </span>
                </span>
              ))}
            </button>
          </li>
        ))}
      </ul>
      {q.hasNextPage && (
        <div className="flex justify-center border-t border-borda p-3">
          <Botao variante="fantasma" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
            {t('comum.carregarMais')}
          </Botao>
        </div>
      )}
    </div>
  );
}
